import { Clear } from '@mui/icons-material';
import { Autocomplete, autocompleteClasses, Checkbox, Chip, createFilterOptions, Popper, TextField, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { action, observable, makeObservable, runInAction } from 'mobx';
import {  observer } from 'mobx-react';
import React from 'react';
import isEqual from 'lodash/isEqual';
import { IAutocomplete } from '../modelo/Autocomplete';
import LayoutStore from '../store/LayoutStore';
import './css/hotkeys.css';

interface IPropsAutoComplete<T> {
    options: IAutocomplete<T>[];
    id: string;
    callback: ((evento, retorno: IAutocomplete<T>) => any) | ((evento, retorno: IAutocomplete<T>[]) => any);
    callbakcValorTexto?: (textoRetornado: string) => any
    label?: string;
    disabled?: boolean;
    apresentarTextoSecundario?: boolean;
    multiple?: boolean;
    freeSolo?: boolean;
    width?: string;
    defaultValue?: IAutocomplete<T> | IAutocomplete<T>[];
    required?: boolean;
    onFocus?: () => void;
    loading?: boolean;
    value?: T;
    white?: boolean;
    onBlur?: () => void;
    focusOnMount?: boolean;
    erro?: boolean;
    maxLength?: number;
    limitOptions?: number;
}

const CssTextField = withStyles({
    root: {
        '& label.Mui-focused': {
            color: 'white',
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: 'yellow',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: 'white',
            },
            '&:hover fieldset': {
                borderColor: 'white',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'white',
            },
        },
        '& .MuiSvgIcon-root': {
            color: 'grey'
        },
        '& .MuiAutocomplete-tag': {
            margin: '2px',
            maxWidth: 'calc(100% - 4px)',
            backgroundColor: 'white'
        }
    }
})(TextField);


class AutoComplete<T> extends React.Component<IPropsAutoComplete<T>> {
    inputRef = React.createRef();
    valueSingle: IAutocomplete<T> = {
        texto: '',
        valor: undefined as T,
        parametrosBusca: ['']
    };
    open = false;
    inputValue = '';
    valueMult: IAutocomplete<T>[] = [];

    constructor(props: IPropsAutoComplete<T>) {
        super(props);

        makeObservable(this, {
            valueSingle: observable,
            open: observable,
            inputValue: observable,
            valueMult: observable,
            onChange: action,
            changeText: action,
            onInputChange: action,
            onOpen: action,
            onBlur: action
        });
    }

    componentDidMount() {
        if (this.props.focusOnMount) {
            (this.inputRef?.current as any)?.focus();
            this.open = true;
        }
        if (this.props.defaultValue && this.props.multiple) {
            this.valueMult = this.props.defaultValue as IAutocomplete<T>[];
        } else {
            this.valueMult = [];
            if (!this.props.multiple) {
                this.valueSingle = this.props.defaultValue as IAutocomplete<T> || {
                    texto: '',
                    valor: undefined as T,
                    parametrosBusca: ['']
                };
            }
        }
    }

    componentDidUpdate(prevProps: IPropsAutoComplete<T>) {
        if (!isEqual(prevProps.defaultValue, this.props.defaultValue)) {
            if (this.props.defaultValue && this.props.multiple) {
                if (!isEqual(this.valueMult, this.props.defaultValue)) {
                    runInAction(() => {
                        this.valueMult = this.props.defaultValue as IAutocomplete<T>[];
                    });
                }
            } else {
                if (this.props.multiple) {
                    if (this.valueMult && this.valueMult.length > 0) {
                        runInAction(() => {
                            this.valueMult = [];
                        });
                    }
                } else {
                    const novoSingle = (this.props.defaultValue as IAutocomplete<T>) || {
                        texto: '',
                        valor: undefined as T,
                        parametrosBusca: ['']
                    };
                    if (!isEqual(this.valueSingle, novoSingle)) {
                        runInAction(() => {
                            this.valueSingle = novoSingle;
                        });
                    }
                }
            }
        }
    }

    normalizarString(string) {
        return string ? string.normalize('NFD').replace(/[\u0300-\u036f]/g, "").toLowerCase() : '';
    }

    highlightTexto = (texto: string, parametro: string, opcao: IAutocomplete<T>) => {
        let inicioHighlight = 0;
        let fimHighlight = 0;
        if (parametro && texto) {
            const corDeDestaque = opcao.foundTextColor ? opcao.foundTextColor : LayoutStore.highlightTexto;
            inicioHighlight = this.normalizarString(texto).indexOf(parametro.toLowerCase());
            if (inicioHighlight < 0) {
                return texto;
            }
            fimHighlight = inicioHighlight + parametro.length;
            if (inicioHighlight !== fimHighlight) {
                texto = texto.substring(0, inicioHighlight) + '<span style="color:' + corDeDestaque + '">' + texto.substring(inicioHighlight, fimHighlight) + '</span>' + texto.substring(fimHighlight, texto.length)
            }
        }
        return texto;
    }

    filterOptions = createFilterOptions({
        matchFrom: 'any',
        stringify: (option: IAutocomplete<T>) => option.parametrosBusca.join(' '),
        limit: this.props.limitOptions
    })

    onChange = (ev, newValue) => {
        let novoValor = [];
        if (this.props.multiple) {
            if (this.props.freeSolo) {
                newValue.forEach(value => {
                    if (typeof value === 'string') {
                        novoValor.push({
                            texto: value,
                            valor: value,
                            parametrosBusca: [value]
                        });
                    } else {
                        novoValor.push(value);
                    }
                });
            } else {
                novoValor = newValue;
            }
            this.valueMult = novoValor;
        } else {
            this.valueSingle = newValue;
        }

        this.props.callback(ev, newValue);
    };

    changeText = (ev) => {
        this.props.callbakcValorTexto && this.props.callbakcValorTexto(ev.target.value);
    };

    onInputChange = (_ev, newInputValue) => {
        if (this.props.multiple && this.inputValue !== newInputValue) {
            this.inputValue = newInputValue;
        }
    };

    onOpen = () => {
        this.open = true;
        this.props.onFocus && this.props.onFocus();
    };

    renderOption = (props, opcao: IAutocomplete<T>, { selected, inputValue, index }) => {
        const { key, ...optionProps } = props;

        return (
            <div key={`${key}-${index}`}>
                <Typography  {...optionProps}
                    component='div'
                >
                    {this.props.multiple &&
                        <Checkbox
                            color='primary'
                            style={{ marginRight: 8 }}
                            checked={selected}
                        />}
                    <div className='divFlexColumn'>
                        <div dangerouslySetInnerHTML={{ __html: this.highlightTexto(opcao.texto, inputValue, opcao) }} style={{ backgroundColor: opcao.backgroundColor ? opcao.backgroundColor : '', color: opcao.textColor ? opcao.textColor : '', fontWeight: opcao.fontWeight ? opcao.fontWeight : '' }} />
                        {this.props.apresentarTextoSecundario ?
                            <div style={{ fontSize: '14px', color: 'grey' }}
                                dangerouslySetInnerHTML={{ __html: this.highlightTexto(opcao.textoSecundario, inputValue, opcao) }} />
                            :
                            null
                        }
                    </div>
                </Typography>
            </div>
        );
    }

    renderInput = (params) => {
        const sufix = this.props.required ? ' *' : '';
        let myLabel = this.props.label;
        const { InputProps, inputProps, ...textFieldParams } = params;
        const onBlur = (ev) => {
            inputProps.onBlur && inputProps.onBlur(ev);
            this.onBlur(ev);
        }
        if (myLabel) {
            myLabel = myLabel + sufix;
        }
        if (this.props.white) {
            return <CssTextField
                {...textFieldParams}
                variant="outlined"
                onChange={this.changeText}
                label={myLabel}
                placeholder={this.props.label}
                inputRef={this.inputRef}
                slotProps={{
                    input: {
                        ...InputProps
                    },
                    htmlInput: {
                        ...inputProps,
                        onBlur
                    }
                }}
            />
        } else {
            return <TextField {...textFieldParams} error={this.props.erro} variant="outlined" onChange={this.changeText} label={myLabel} placeholder={this.props.label} inputRef={this.inputRef}
                slotProps={{
                    input: {
                        ...InputProps
                    },
                    htmlInput: {
                        ...inputProps,
                        onBlur,
                        maxLength: this.props.maxLength
                    }
                }}
            />
        }
    }

    getOptionLabel = (opcao: IAutocomplete<T>) => {
        return opcao?.texto || '';
    }

    handleOptionEqualToValue = (option, value) => {
        return option.texto === value.texto;
    }

    onBlur = (_ev) => {
        if (this.props.multiple && this.props.freeSolo) {
            this.inputValue = '';
        }
        this.open = false;
        this.props.onBlur && this.props.onBlur();
    }

    renderPopper = props => {
        const widthAutocomplete = document.getElementById(this.props.id + '-pai');
        return <Popper id='teste' {...props} placement='bottom-start'
            sx={{
                [`& .${autocompleteClasses.listbox}`]: {
                    boxSizing: 'border-box',
                    '& ul': {
                        padding: 0,
                        margin: 0,
                    },
                },
                '& .MuiAutocomplete-paper': {
                    width: '100%',
                },
                width: widthAutocomplete?.clientWidth
            }} style={{ zIndex: 1350 }} />
    }

    ajustarBackgroundDaTagDeQtd() {
        setTimeout(() => {
            const spans = document.querySelectorAll('span.MuiAutocomplete-tag');
            let i = 0;
            for (i = 0; i < spans.length; i++) {
                (spans[i] as any).style.backgroundColor = 'inherit'
            }
        }, 0);
    }

    render() {
        this.ajustarBackgroundDaTagDeQtd();
        const props = {
            id: this.props.id,
            options: this.props.options,
            disabled: this.props.disabled,
            disableCloseOnSelect: !!this.props.multiple,
            filterOptions: this.filterOptions,
            onChange: this.onChange,
            getOptionLabel: this.getOptionLabel,
            renderOption: this.renderOption,
            renderInput: this.renderInput,
            style: { width: this.props.width || '90%' },
            clearText: 'Limpar',
            freeSolo: !!this.props.freeSolo,
            closeText: 'Fechar',
            openText: 'Abrir',
            noOptionsText: 'Nenhuma opção',
            loading: this.props.loading,
            loadingText: 'Carregando...',
            clearIcon: <Clear name='AutocompleteGE' fontSize="small" sx={{ color: this.props.white ? 'white' : 'rgba(0, 0, 0, 0.54)' }} />,
            onOpen: this.onOpen,
            onInputChange: this.onInputChange,
            inputValue: this.props.multiple ? this.inputValue : undefined,
            isOptionEqualToValue: this.handleOptionEqualToValue,
            limitTags: 10,
            // inputProps: { onBlur: this.props.onBlur },
            // InputLabelProps: { style: { color: 'red' } },
            open: this.open,
            onClose: action(() => this.open = false)
        };
        if (this.props.multiple) {
            return (
                <div id={this.props.id + '-pai'}>
                    <Autocomplete
                        size='small'
                        multiple={true}
                        {...props}
                        value={this.valueMult.slice()}
                        sx={{
                            borderColor: this.props.white ? 'white' : '',
                            ".MuiOutlinedInput-root": {
                                "&:hover": {
                                    borderColor: "white",
                                }
                            },
                            ".MuiChip-deleteIconSmall": {
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 'inherit',
                                borderBottomLeftRadius: 0,
                                borderBottomRightRadius: 'inherit'
                            }
                        }}
                    />
                </div>
            );
        }
        return (
            <div id={this.props.id + '-pai'} style={{ width: '100%' }}>
                <Autocomplete
                    size='small'
                    multiple={false}
                    {...props}
                    value={this.valueSingle}
                    PopperComponent={this.renderPopper}
                    sx={{
                        borderColor: this.props.white ? 'white' : '',
                        ".MuiOutlinedInput-root": {
                            "&:hover": {
                                borderColor: "white",
                            }
                        }
                    }}
                //variant="outlined"
                />
            </div>
        );
    }
}
export default observer(AutoComplete);
